import { z } from 'zod';
import { IsoTimestamp, RunId, UserId } from './common';

const APPLICATION_OUTCOME_VALUES = [
  'applied',
  'skipped_filter',
  'skipped_already_applied',
  'failed_form',
  'failed_login',
  'failed_other',
  'pending_verification',
  // Worker started the application but couldn't finish autonomously
  // (no answer-bank match for a hard question, mid-form LLM 429, etc.)
  // The run summary surfaces these so the user can finish them in their
  // browser via the LinkedIn URL.
  'needs_user_completion',
] as const;
export const ApplicationOutcome = z.enum(APPLICATION_OUTCOME_VALUES);
export type ApplicationOutcome = z.infer<typeof ApplicationOutcome>;

/** Forward-compat read variant — see #482. */
export const ApplicationOutcomeReadable = z
  .enum([...APPLICATION_OUTCOME_VALUES, 'unknown'] as const)
  .catch('unknown');
export type ApplicationOutcomeReadable = z.infer<typeof ApplicationOutcomeReadable>;

export const Application = z.object({
  userId: UserId,
  linkedinJobId: z.string(),
  runId: RunId,
  title: z.string(),
  company: z.string(),
  location: z.string().optional(),
  url: z.string().url(),
  externalUrl: z.string().url().optional(),
  hostname: z.string().optional(),
  pathname: z.string().optional(),
  outcome: ApplicationOutcomeReadable,
  isEasyApply: z.boolean(),
  reason: z.string().optional(),
  /**
   * If the attempt failed, points at the S3 prefix where the worker
   * uploaded screenshot.png + dom.html + console.log. Lets us review
   * failures from a dashboard without keeping the browser tab open.
   */
  snapshotS3Prefix: z.string().optional(),
  appliedAt: IsoTimestamp,
});

export type Application = z.infer<typeof Application>;

/**
 * Worker → API POST body. `outcome` is overridden to the strict enum
 * so the write path continues to reject unknown values even though the
 * read shape now folds them into `'unknown'` for forward-compat (#482).
 */
export const CreateApplicationRequest = Application.omit({
  userId: true,
  appliedAt: true,
}).extend({
  outcome: ApplicationOutcome,
});
export type CreateApplicationRequest = z.infer<typeof CreateApplicationRequest>;
